import { PaginationApiType } from '@shared/types/data-table.types';
import {
  EdgeAPI,
  NodeAPI,
  type NodeMeta,
} from '@app/pages/audience-manager/pages/builder/shared/models/builder-data.model';

export enum FrequencyValues {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
}

export enum ExportType {
  IMMEDIATE = 'immediate',
  SCHEDULED = 'scheduled',
}

export enum ScheduledType {
  ONE_TIME = 'one_time',
  RECURRING = 'recurring',
}

export enum ExportStatus {
  COMPLETED = 'completed',
  FAILED = 'failed',
  RUNNING = 'running',
  SCHEDULED = 'scheduled',
  CANCELLED = 'cancelled',
}

export interface Export {
  id: string;
  exportName: string;
  audienceName: string;
  exportType: ExportType;
  status: ExportStatus;
  scheduledType: ScheduledType | null;
  startedDate: Date | null;
  lastRun: Date | null;
  currentRuns: number;
  records: number;
  initiatedBy: string;
}

export interface GetExportsApiResponse {
  exports: Export[];
  pagination: PaginationApiType;
}
export interface ScheduledConfig {
  scheduleType: ScheduledType;
  startDate: string;
  endDate: string | null;
  time: string;
  frequency: FrequencyValues | null;
  /** IANA timezone string selected by the user (e.g. 'America/Chicago'). Defaults to the browser timezone. */
  timezone: string;
}

export enum DestinationDisabledReason {
  COMING_SOON = 'COMING_SOON',
  ACTIVE_EXPORT = 'ACTIVE_EXPORT',
  TENANT_NOT_AVAILABLE = 'TENANT_NOT_AVAILABLE',
}

export enum ExportDestinationKey {
  GOOGLE_BIGQUERY = 'googleBigQuery',
  DSP = 'DSP',
  META = 'meta',
  AMAZON_S3 = 'amazonS3',
  GOOGLE_CLOUD_STORAGE = 'googleCloudStorage',
}

/**
 * Meta customer_file_source values.
 * Stirista start-export / create-audience APIs use `3PD` for MVP.
 * Graph-style enum values remain for future 1PD / combination work.
 */
export enum CustomerFileSource {
  /** Stirista MVP 3PD-only provenance sent on Meta export APIs. */
  THREE_PD = '3PD',
  USER_PROVIDED_ONLY = 'USER_PROVIDED_ONLY',
  PARTNER_PROVIDED_ONLY = 'PARTNER_PROVIDED_ONLY',
  BOTH_USER_AND_PARTNER_PROVIDED = 'BOTH_USER_AND_PARTNER_PROVIDED',
}

/**
 * Meta audience data types surfaced in the export "Data Type" section.
 * MVP supports 3PD only; 1PD and combination are intentionally out of scope.
 */
export enum MetaDataType {
  THIRD_PARTY_ONLY = '3pd_only',
}

/** Fixed Meta Custom Audience subtype (spec §4.1). */
export const META_CUSTOM_AUDIENCE_SUBTYPE = 'CUSTOM';

export enum ExportWritePreference {
  APPEND = 'append',
  TRUNCATE = 'truncate',
  REGENERATE = 'regenerate',
}

/**
 * Response from GET active-export-destinations/count for an audience.
 * When count is greater than zero, exportId is used to deep-link Job History.
 */
export interface ActiveExportDestinationsCountResponse {
  count: number;
  exportId?: string | null;
}

/** Destination option rendered in the destination picker grid */
export interface DestinationOption {
  id: string;
  key?: ExportDestinationKey;
  name: string;
  description: string;
  selected?: boolean;
  isDisabled: boolean;
  /** When true, the destination is unavailable for the current tenant. */
  isTenantDisabled?: boolean;
  disabledReason?: DestinationDisabledReason;
}

export interface GoogleBigQueryDestination {
  id: string;
  dataset: string;
  table: string;
  writePreference: ExportWritePreference;
}

export interface DspDestination {
  id: string;
  segmentName: string;
  ttl: number | null;
}

/**
 * Meta Custom Audience destination configuration (spec §4.1 + Stirista Meta APIs).
 * `name` and `subtype` are derived at payload / create time (audience name + CUSTOM).
 * `audienceMode` and `customAudienceName` are UI draft fields and must be omitted
 * from the create-export API payload.
 */
export interface MetaDestination {
  id: string;
  /** Selected Meta ad account id for this export. */
  accountId: string | null;
  /** Target Meta Custom Audience id (created or selected). */
  audienceId: string | null;
  /** User-generated Custom Audience description. */
  description: string;
  /** Data provenance declaration; fixed to `3PD` for MVP. */
  customerFileSource: CustomerFileSource;
  /** Selected Meta audience data type (MVP: 3PD only). */
  dataType: MetaDataType;
  /**
   * UI-only: create a new Custom Audience vs select an existing one.
   * Omitted from the create-export API payload.
   */
  audienceMode?: 'create' | 'existing';
  /**
   * UI-only: name used when `audienceMode` is `create`.
   * Omitted from the create-export API payload.
   */
  customAudienceName?: string;
}

export interface ExportDestinationsConfig {
  googleBigQuery: GoogleBigQueryDestination | null;
  DSP: DspDestination | null;
  meta: MetaDestination | null;
  amazonS3: null;
  googleCloudStorage: null;
}

/**
 * Backend contract identifiers for attachable PII fields.
 * These are not builder attributes and must not be loaded from BuilderApiService.
 */
export const PII_ATTACH_FIELD_IDS = {
  FIRST_NAME: 'first_name',
  LAST_NAME: 'last_name',
  ADDRESS: 'address',
  ADDRESS_2: 'address_2',
  CITY: 'city',
  STATE: 'state',
  ZIP: 'zip',
  EMAIL: 'email',
  PHONE: 'phone',
} as const;

export type PiiAttachFieldId =
  (typeof PII_ATTACH_FIELD_IDS)[keyof typeof PII_ATTACH_FIELD_IDS];

/**
 * Prefix for UI selection keys of attachable PII rows.
 * Contract `id` values are unchanged; this only distinguishes PII from builder rows.
 */
export const PII_ATTACH_SELECTION_KEY_PREFIX = 'pii:';

/**
 * Stable UI selection identity for an export field row.
 * Builder attributes use `id`; attachable PII uses `pii:{id}` so both can be
 * selected when they share a contract id such as `email`.
 * @param field - Field with contract `id` and optional PII flag.
 * @returns Selection key unique across builder and PII rows.
 */
export function exportFieldSelectionKey(field: {
  id: string;
  isPiiAttach?: boolean;
}): string {
  return field.isPiiAttach === true
    ? `${PII_ATTACH_SELECTION_KEY_PREFIX}${field.id}`
    : field.id;
}

/**
 * Resolves a selection key from a contract id string or a field object.
 * Plain strings are treated as builder (or Stirista ID) keys, not PII keys.
 * @param fieldOrId - Builder field id, or a field carrying `isPiiAttach`.
 * @returns Selection key used by field-selection add/remove/lookup.
 */
export function resolveExportFieldSelectionKey(
  fieldOrId: string | { id: string; isPiiAttach?: boolean },
): string {
  return typeof fieldOrId === 'string'
    ? fieldOrId
    : exportFieldSelectionKey(fieldOrId);
}

/**
 * Synthetic field-selection category id for attachable PII.
 * Negative so it cannot collide with builder category ids from the API.
 */
export const PII_ATTACH_CATEGORY_ID = -1;

/** PII attach field ids grouped by tenant misc bundle permission. */
export const PII_ATTACH_BUNDLE_FIELD_IDS = {
  piiBundleNamePostal: [
    PII_ATTACH_FIELD_IDS.FIRST_NAME,
    PII_ATTACH_FIELD_IDS.LAST_NAME,
    PII_ATTACH_FIELD_IDS.ADDRESS,
    PII_ATTACH_FIELD_IDS.ADDRESS_2,
    PII_ATTACH_FIELD_IDS.CITY,
    PII_ATTACH_FIELD_IDS.STATE,
    PII_ATTACH_FIELD_IDS.ZIP,
  ],
  piiBundleEmail: [PII_ATTACH_FIELD_IDS.EMAIL],
  piiBundlePhone: [PII_ATTACH_FIELD_IDS.PHONE],
} as const;

export interface ExportField {
  id: string;
  label: string;
  required?: boolean;
  description?: string;
  type?: string[];
  categoryId?: number;
  sourceLabel?: string | undefined;
  /** True when this field is attachable PII (later mapped to piiAttach.fields). */
  isPiiAttach?: boolean;
}

/** PII Email/Phone value multiplicity for the attach draft. */
export type PiiValueMultiplicity = 'primary' | 'all';

/** Selected PII Email/Phone multiplicity stored on the create-export draft. */
export interface PiiAttachDraft {
  emailMultiplicity?: PiiValueMultiplicity;
  phoneMultiplicity?: PiiValueMultiplicity;
}

/** PII attach object sent on POST /tenants/{tenantId}/exports/start. */
export interface PiiAttachPayload {
  fields: string[];
  grain: 'individual';
  emailMultiplicity?: 'primary' | 'all';
  phoneMultiplicity?: 'primary' | 'all';
  allValuesFormat?: 'expanded_rows' | 'id_grain_arrays';
}

export interface CreateExport {
  audienceId: string;
  audienceName: string | null;
  fields: ExportField[];
  exportType: ExportType;
  scheduledConfig: ScheduledConfig | null;
  exportName: string;
  selectedDestinationKey: ExportDestinationKey | null;
  destination: ExportDestinationsConfig;
  nodes?: NodeAPI[];
  edges?: EdgeAPI[];
  /** PII Email/Phone multiplicity draft; mapped to API piiAttach in getExportPayload. */
  piiAttach?: PiiAttachDraft | null;
}

export interface CreateExportApiPayload {
  audienceId: string;
  audienceName: string | null;
  fields: { id: string; sourceLabel: string }[];
  exportType: ExportType;
  scheduledConfig: ScheduledConfig | null;
  exportName: string;
  destination: ExportDestinationsConfig;
  piiAttach?: PiiAttachPayload;
}

export interface StepperConfig {
  label: string;
  value: number;
}

export interface exportField {
  // This export field id is the attribute in a different structure
  id: string;
  label: string;
  categoryId: number;
  required: boolean;
  sourceLabel: string | undefined;
  /** True when this field is attachable PII (later mapped to piiAttach.fields). */
  isPiiAttach?: boolean;
}

export interface exportCategory {
  id: number;
  label: string;
}

export interface GetFieldsApiResponse {
  categories: exportCategory[];
  fields: exportField[];
}

export interface SearchedFieldItem {
  id: string;
  label: string;
  meta: string;
}

export interface SearchedFieldItemApiResponse {
  id: string;
  name: string;
  meta: string;
}

export interface SearchedFieldsApiResponse {
  label: string;
  value: string;
  items: SearchedFieldItemApiResponse[];
}

export interface SearchedFields {
  label: string;
  value: string;
  items: SearchedFieldItem[];
}

/**
 * Builder search item shape that supports both attributes and custom audiences.
 */
export interface BuilderSearchedFieldItem extends Omit<
  SearchedFieldItem,
  'meta'
> {
  name?: string;
  meta?: string | NodeMeta;
  nodeType?: 'audience' | 'lookalike';
  size?: number | string;
  description?: string;
  sizeUpdatedAt?: string;
}

/**
 * Grouped builder search results shape.
 */
export interface BuilderSearchedFields {
  label: string;
  value: string;
  items: BuilderSearchedFieldItem[];
}

export interface DatasetsGetResponse {
  datasets: string[];
}

export interface TableListGetResponse {
  tables: string[];
}
